import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SearchBar } from '../components/SearchBar';
import { apiClient } from '../services/api';

interface SearchResult {
  id: string;
  title: string;
  content: string;
  type: string;
  category: string;
  tags?: string[];
  created_at: string;
  highlight?: {
    title?: string;
    content?: string;
  };
}

export default function SearchResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const typeFilter = searchParams.get('type') || 'all';

  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    if (query) {
      performSearch();
    }
  }, [query, typeFilter, page]);

  const performSearch = async () => {
    setIsLoading(true);

    try {
      const params = new URLSearchParams({
        q: query,
        limit: pageSize.toString(),
        offset: ((page - 1) * pageSize).toString(),
      });

      if (typeFilter !== 'all') {
        params.append('type', typeFilter);
      }

      const result = await apiClient.request<{
        results: SearchResult[];
        total: number;
      }>(`/api/search?${params.toString()}`);

      if (result.data) {
        setResults(result.data.results);
        setTotalCount(result.data.total);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (newQuery: string) => {
    navigate(`/search?q=${encodeURIComponent(newQuery)}`);
    setPage(1);
  };

  const handleTypeFilter = (type: string) => {
    const params = new URLSearchParams({ q: query });
    if (type !== 'all') {
      params.append('type', type);
    }
    navigate(`/search?${params.toString()}`);
    setPage(1);
  };

  const handleResultClick = (result: SearchResult) => {
    switch (result.type) {
      case 'bookmark':
        navigate(`/bookmarks`);
        break;
      case 'note':
        navigate(`/notes/${result.id}`);
        break;
      case 'expense':
        navigate(`/expenses`);
        break;
      case 'schedule':
        navigate(`/schedule`);
        break;
      case 'reminder':
        navigate(`/reminders`);
        break;
      default:
        navigate(`/notes/${result.id}`);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'bookmark': return '🔖';
      case 'note': return '📝';
      case 'expense': return '💰';
      case 'schedule': return '📅';
      case 'reminder': return '⏰';
      default: return '📄';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'bookmark': return '书签';
      case 'note': return '笔记';
      case 'expense': return '记账';
      case 'schedule': return '日程';
      case 'reminder': return '提醒';
      default: return '内容';
    }
  };

  const highlightText = (text: string, highlight?: string) => {
    if (!highlight) return text;
    
    // Simple highlight - in production, use proper HTML sanitization
    return highlight;
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="search-results-page">
      <div className="search-header">
        <SearchBar
          onSearch={handleSearch}
          placeholder="搜索书签、笔记、日程..."
          autoFocus={false}
        />
      </div>

      {query && (
        <>
          <div className="search-info">
            <h2>搜索结果</h2>
            <p>找到 {totalCount} 条关于 "{query}" 的结果</p>
          </div>

          <div className="type-filters">
            <button
              className={typeFilter === 'all' ? 'active' : ''}
              onClick={() => handleTypeFilter('all')}
            >
              全部 ({totalCount})
            </button>
            <button
              className={typeFilter === 'bookmark' ? 'active' : ''}
              onClick={() => handleTypeFilter('bookmark')}
            >
              🔖 书签
            </button>
            <button
              className={typeFilter === 'note' ? 'active' : ''}
              onClick={() => handleTypeFilter('note')}
            >
              📝 笔记
            </button>
            <button
              className={typeFilter === 'expense' ? 'active' : ''}
              onClick={() => handleTypeFilter('expense')}
            >
              💰 记账
            </button>
            <button
              className={typeFilter === 'schedule' ? 'active' : ''}
              onClick={() => handleTypeFilter('schedule')}
            >
              📅 日程
            </button>
            <button
              className={typeFilter === 'reminder' ? 'active' : ''}
              onClick={() => handleTypeFilter('reminder')}
            >
              ⏰提醒
            </button>
          </div>

          <div className="results-container">
            {isLoading ? (
              <div className="loading">搜索中...</div>
            ) : results.length === 0 ? (
              <div className="no-results">
                <p>未找到相关内容</p>
                <p>尝试使用不同的关键词</p>
              </div>
            ) : (
              <>
                <div className="results-list">
                  {results.map(result => (
                    <div
                      key={result.id}
                      className="result-item"
                      onClick={() => handleResultClick(result)}
                    >
                      <div className="result-icon">
                        {getTypeIcon(result.type)}
                      </div>
                      <div className="result-content">
                        <div className="result-header">
                          <h3
                            className="result-title"
                            dangerouslySetInnerHTML={{
                              __html: highlightText(result.title, result.highlight?.title),
                            }}
                          />
                          <span className="result-type">
                            {getTypeLabel(result.type)}
                          </span>
                        </div>
                        <p
                          className="result-snippet"
                          dangerouslySetInnerHTML={{
                            __html: highlightText(
                              result.content.substring(0, 200),
                              result.highlight?.content
                            ),
                          }}
                        />
                        <div className="result-meta">
                          {result.tags && result.tags.length > 0 && (
                            <div className="result-tags">
                              {result.tags.slice(0, 3).map((tag, index) => (
                                <span key={index} className="result-tag">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          <span className="result-date">
                            {new Date(result.created_at).toLocaleDateString('zh-CN')}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="pagination-btn"
                    >
                      ← 上一页
                    </button>
                    <span className="pagination-info">
                      第 {page} / {totalPages} 页
                    </span>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="pagination-btn"
                    >
                      下一页 →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      <style>{`
        .search-results-page {
          padding: 24px;
          max-width: 900px;
          margin: 0 auto;
        }

        .search-header {
          margin-bottom: 32px;
        }

        .search-info {
          margin-bottom: 24px;
        }

        .search-info h2 {
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 8px 0;
          color: #333;
        }

        .search-info p {
          font-size: 14px;
          color: #666;
          margin: 0;
        }

        .type-filters {
          display: flex;
          gap: 8px;
          margin-bottom: 24px;
          overflow-x: auto;
          padding-bottom: 8px;
        }

        .type-filters button {
          padding: 8px 16px;
          border: 1px solid #e0e0e0;
          background: white;
          border-radius: 20px;
          font-size: 14px;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.2s;
        }

        .type-filters button:hover {
          background: #f5f5f5;
        }

        .type-filters button.active {
          background: #007bff;
          color: white;
          border-color: #007bff;
        }

        .results-container {
          background: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .results-list {
          padding: 8px;
        }

        .result-item {
          display: flex;
          gap: 16px;
          padding: 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .result-item:hover {
          background: #f9f9f9;
        }

        .result-icon {
          font-size: 32px;
          flex-shrink: 0;
        }

        .result-content {
          flex: 1;
          min-width: 0;
        }

        .result-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 8px;
        }

        .result-title {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
          color: #333;
          flex: 1;
        }

        .result-title mark {
          background: #ffeb3b;
          padding: 2px 4px;
          border-radius: 2px;
        }

        .result-type {
          font-size: 11px;
          color: #999;
          padding: 2px 8px;
          background: #f0f0f0;
          border-radius: 10px;
          flex-shrink: 0;
        }

        .result-snippet {
          font-size: 14px;
          color: #666;
          line-height: 1.6;
          margin: 0 0 8px 0;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        .result-snippet mark {
          background: #ffeb3b;
          padding: 2px 4px;
          border-radius: 2px;
        }

        .result-meta {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 12px;
          color: #999;
        }

        .result-tags {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        .result-tag {
          padding: 2px 8px;
          background: #e3f2fd;
          color: #007bff;
          border-radius: 10px;
          font-size: 11px;
        }

        .result-date {
          margin-left: auto;
        }

        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 16px;
          padding: 20px;
          border-top: 1px solid #f0f0f0;
        }

        .pagination-btn {
          padding: 8px 16px;
          border: 1px solid #e0e0e0;
          background: white;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .pagination-btn:hover:not(:disabled) {
          background: #f5f5f5;
          border-color: #007bff;
          color: #007bff;
        }

        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-info {
          font-size: 14px;
          color: #666;
        }

        .loading,
        .no-results {
          text-align: center;
          padding: 60px 20px;
          color: #999;
        }

        .no-results p {
          margin: 8px 0;
        }

        @media (max-width: 768px) {
          .search-results-page {
            padding: 16px;
          }

          .result-item {
            flex-direction: column;
          }

          .result-icon {
            font-size: 24px;
          }

          .type-filters {
            flex-wrap: nowrap;
          }
        }
      `}</style>
    </div>
  );
}
